<template>
  <v-form ref="form" v-model="isFormValid">
    <div class="back-btn" @click="navigateToIndex">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M36 27H23.83L29.42 21.41L28 20L20 28L28 36L29.41 34.59L23.83 29H36V27Z" fill="#8C959D"/>
      </svg>
    </div>

    <v-row>
      <v-col cols="12">
        <h1>{{ pageTitle }}</h1>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-text>
            <v-row>

              <!-- Название (для удобства в админке) -->
              <v-col cols="12">
                <v-text-field
                    v-model="form.name"
                    label="Название аккаунта"
                    outlined
                    :rules="requiredRules"
                    hint="Например: Касса №1, Центральный офис"
                    persistent-hint
                />
              </v-col>

              <!-- Логин кассира -->
              <v-col cols="12">
                <v-text-field
                    v-model="form.login"
                    label="Логин кассира (CHECKBOX_CASHIER_LOGIN)"
                    outlined
                    :rules="requiredRules"
                    autocomplete="off"
                />
              </v-col>

              <!-- Пароль кассира -->
              <v-col cols="12">
                <v-text-field
                    v-model="form.password"
                    label="Пароль кассира (CHECKBOX_CASHIER_PASSWORD)"
                    outlined
                    :rules="isEditMode ? [] : requiredRules"
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    :hint="isEditMode ? 'Оставьте пустым, чтобы не менять пароль' : ''"
                    persistent-hint
                    autocomplete="new-password"
                    @click:append="showPassword = !showPassword"
                />
              </v-col>

              <!-- License Key -->
              <v-col cols="12">
                <v-text-field
                    v-model="form.license_key"
                    label="License Key (CHECKBOX_LICENSE_KEY)"
                    outlined
                    :rules="isEditMode ? [] : requiredRules"
                    :type="showLicenseKey ? 'text' : 'password'"
                    :append-icon="showLicenseKey ? 'mdi-eye-off' : 'mdi-eye'"
                    :hint="isEditMode ? 'Оставьте пустым, чтобы не менять ключ' : ''"
                    persistent-hint
                    autocomplete="off"
                    @click:append="showLicenseKey = !showLicenseKey"
                />
              </v-col>

            </v-row>
          </v-card-text>
        </v-card>

        <!-- Привязанные филиалы (только в режиме редактирования) -->
        <v-card outlined class="mt-4" v-if="isEditMode && linkedBranches.length > 0">
          <v-card-title class="subtitle-1">
            <v-icon left small>mdi-store</v-icon>
            Привязанные филиалы
          </v-card-title>
          <v-list dense>
            <v-list-item v-for="branch in linkedBranches" :key="branch.id">
              <v-list-item-icon>
                <v-icon small>mdi-map-marker</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ branch.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Кнопки -->
        <v-row class="mt-4">
          <v-col>
            <v-btn
                :loading="isButtonLoading"
                :disabled="!isFormValid || isButtonLoading"
                color="primary"
                large
                @click="handleSubmit"
            >
              {{ submitButtonText }}
            </v-btn>
            <v-btn text large class="ml-2" @click="navigateToIndex">
              Отмена
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="4000"
        top right
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Закрыть</v-btn>
      </template>
    </v-snackbar>
  </v-form>
</template>

<script>
import CheckboxLoginService from '@/api/CheckboxLoginService'

export default {
  name: 'CheckboxLoginEdit',

  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      isFormValid: true,
      isButtonLoading: false,
      showPassword: false,
      showLicenseKey: false,

      form: {
        name: '',
        login: '',
        password: '',
        license_key: ''
      },

      linkedBranches: [],

      requiredRules: [v => !!v || 'Нужно заполнить'],

      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },

  computed: {
    isEditMode() {
      return !!this.id
    },
    pageTitle() {
      return this.isEditMode ? 'Редактирование Checkbox аккаунта' : 'Создание Checkbox аккаунта'
    },
    submitButtonText() {
      return this.isEditMode ? 'Сохранить' : 'Создать'
    }
  },

  mounted() {
    if (this.isEditMode) {
      this.loadData()
    }
  },

  methods: {
    navigateToIndex() {
      this.$router.push({ name: 'checkbox-login-index' })
    },

    async loadData() {
      try {
        const response = await CheckboxLoginService.get(this.id)
        const data = response.data
        this.form = {
          name:        data.name,
          login:       data.login,
          password:    '',        // не показываем пароль
          license_key: ''         // не показываем ключ
        }
        this.linkedBranches = data.branches || []
      } catch (error) {
        this.showSnackbar('Ошибка при загрузке данных', 'error')
      }
    },

    async handleSubmit() {
      this.$refs.form.validate()
      if (!this.isFormValid) return

      this.isButtonLoading = true
      try {
        // В режиме редактирования — отправляем только заполненные поля
        const payload = { name: this.form.name, login: this.form.login }
        if (this.form.password)    payload.password    = this.form.password
        if (this.form.license_key) payload.license_key = this.form.license_key

        if (this.isEditMode) {
          await CheckboxLoginService.update(this.id, payload)
          this.showSnackbar('Аккаунт успешно обновлен')
        } else {
          const response = await CheckboxLoginService.create(this.form)
          this.showSnackbar('Аккаунт создан')
          this.$router.push({ name: 'checkbox-login-edit', params: { id: response.data.id } })
        }
      } catch (error) {
        const msg = error.response?.data?.message || 'Ошибка при сохранении'
        this.showSnackbar(msg, 'error')
      } finally {
        this.isButtonLoading = false
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>

<style scoped>
.back-btn {
  cursor: pointer;
  display: inline-block;
  margin-bottom: 16px;
}
.back-btn:hover { opacity: 0.7; }
</style>